import React from "react";
import { Line } from "react-chartjs-2";

const MultiLineChart = ({ graphData, combineData, selectedFilter }) => {
  if (!graphData || graphData.length === 0) {
    return <div>Loading...</div>; // Show loading while data is being fetched
  }

  // Extract labels (dates) for X-axis
  // const labels = graphData.map((data) => {
  //   const date = new Date(data.date);
  //   const month = String(date.getMonth() + 1).padStart(2, "0");
  //   const day = String(date.getDate()).padStart(2, "0");
  //   return `${day}/${month}`;
  // });
  const labels =
    graphData.length > 0
      ? graphData.map((item) => new Date(item.date)) // keep Date objects
      : [];
  function hexToRGBA(hex, opacity) {
    // Remove the hash (#) if it exists
    hex = hex.replace("#", "");

    // Convert the 6-character hex to RGB values
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    // Return the rgba string
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }
  // Create datasets for each stock
  const stockDatasets = {};
  graphData.forEach((data) => {
    data.stock_prices.forEach((stock) => {
      if (!stockDatasets[stock.stock_id]) {
        stockDatasets[stock.stock_id] = {
          label: stock.stock_asx,
          data: [],
          borderColor: `#${stock.color}`, // Use the original hex color
          backgroundColor: hexToRGBA(stock.color, 0.07),
          tension: 0.4,
          fill: true,
          pointRadius: 0,
          pointHoverRadius: 0,
        };
      }
      stockDatasets[stock.stock_id].data.push(stock.price);
    });
  });

  // Add dataset for the total price
  const totalPriceDataset = {
    label: "Total Price",
    data: graphData.map((item) => ({
      x: item.date, // <-- pass date string directly
      y: item.total_price,
    })),
    borderColor: "#3047EC",
    backgroundColor: "rgba(48, 71, 236, 0.07)",
    tension: 0.4,
    pointRadius: 4,
    pointHoverRadius: 6,
    pointBackgroundColor: "#3047EC",
    fill: true,
  };
  const combinedIndexedDataset = {
    label: "Combine index",
    data: combineData.map((item) => ({
      x: item.date, // <-- pass date string directly
      y: item.value,
    })),
    //combineData.map((data) => data.value),
    borderColor: "#DC4646",
    backgroundColor: "rgba(48, 71, 236, 0.07)",
    tension: 0.4,
    pointRadius: 4,
    pointHoverRadius: 6,
    pointBackgroundColor: "#DC4646",
    fill: true,
  };

  // Prepare final datasets array
  const datasets = [
    ...Object.values(stockDatasets),
    totalPriceDataset,
    combinedIndexedDataset,
  ];

  // Chart data
  const data = {
    labels,
    datasets,
  };

  // Chart options
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      tooltip: {
        mode: "index",
        intersect: false,
        backgroundColor: "#111827",
        titleColor: "#fff",
        borderColor: "#374151",
        borderWidth: 1,
        padding: 12,
        displayColors: false, // hoverRadiusHide colored dots
        callbacks: {
          title: (tooltipItems) => {
            const date = new Date(tooltipItems[0].parsed.x);
            if (selectedFilter === "1M" || selectedFilter === "3M") {
              const day = String(date.getDate()).padStart(2, "0");
              const month = date.toLocaleDateString("en-US", {
                month: "short",
              });
              const year = date.getFullYear();
              return `${day} ${month} ${year}`;
            } else {
              const month = date.toLocaleDateString("en-US", {
                month: "short",
              });
              const year = date.getFullYear();
              return `${month} ${year}`;
            }
          },
          label: (tooltipItem) => {
            const value = `$${tooltipItem.raw.y
              .toFixed(2)
              .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
            return value;
          },
          labelTextColor: function (context) {
            const index = context.datasetIndex;
            return ["#3047EC", "#DC4646", "#34C046"][index];
          },
        },
      },
      legend: { display: false },
    },
    scales: {
      x: {
        type: "time", // ⬅️ critical: interpret x as time
        time: {
          unit:
            selectedFilter === "1M" || selectedFilter === "3M"
              ? "day"
              : "month",
          displayFormats: {
            day: "dd MMM", // e.g. "10 Aug"
            month: "MMM yyyy", // e.g. "Aug 2025"
          },
        },
        ticks: {
          color: "black",
          callback: function (value) {
            const date = new Date(value);
            if (selectedFilter === "1M" || selectedFilter === "3M") {
              const day = String(date.getDate()).padStart(2, "0"); // "10"
              const month = date.toLocaleDateString("en-US", {
                month: "short",
              }); // "Aug"
              return [day, month]; // <-- stacked lines
            } else {
              const month = date.toLocaleDateString("en-US", {
                month: "short",
              }); // "Aug"
              const year = date.getFullYear(); // "2025"
              return [month, year]; // <-- stacked lines
            }
          },
        },
        grid: { drawOnChartArea: false, drawTicks: false },
        border: { color: "black" },
      },

      y: {
        grid: { drawOnChartArea: false, drawTicks: false },
        ticks: {
          callback: (value) => `${value.toFixed(2)}`,
          color: "black",
          padding: 10, // Add spacing for Y-axis labels
        },
        beginAtZero: false, // Optional: prevent starting at zero
        min: 0, // Adjust this value to add more space above the border
        offset: true, // Add extra space between the border and the graph
        border: { display: false },
      },
    },
  };

  return (
    <div style={{ width: "100%", height: "400px" }}>
      <Line data={data} options={options} />
    </div>
  );
};

export default MultiLineChart;
